/**
 * Stylish Car -- Shopify Theme JavaScript
 * Replaces all Angular interactivity with vanilla JS.
 * Modules: Menu, Scroll, BackButton, FindKit, CatalogFilter, Gallery, Cart, FloatingCartBar
 */
(function () {
  'use strict';

  /* --- Utility ----------------------------------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* --- State ------------------------------------- */
  const state = {
    cartCount: window.StylishCar?.cartCount || 0,
    cartTotal: 0,
    lastScrollY: 0,
    menuOpen: false,
  };

  /* ================================================
     1. FULLSCREEN MENU
     ================================================ */
  function initMenu() {
    const toggle = $('#menu-toggle');
    const menu = $('#fullscreen-menu');
    if (!toggle || !menu) return;

    const backdrop = $('#menu-backdrop');
    const panel = $('#menu-panel');

    function openMenu() {
      state.menuOpen = true;
      menu.classList.remove('invisible', 'opacity-0');
      menu.classList.add('opacity-100');
      menu.setAttribute('aria-hidden', 'false');
      // Backdrop fade in
      if (backdrop) {
        backdrop.classList.remove('opacity-0');
        backdrop.classList.add('opacity-100');
      }
      // Panel slide in (from left)
      if (panel) {
        panel.classList.remove('-translate-x-full');
        panel.classList.add('translate-x-0');
      }
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
    }

    function closeMenu() {
      state.menuOpen = false;
      if (backdrop) {
        backdrop.classList.remove('opacity-100');
        backdrop.classList.add('opacity-0');
      }
      if (panel) {
        panel.classList.remove('translate-x-0');
        panel.classList.add('-translate-x-full');
      }
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        menu.classList.remove('opacity-100');
        menu.classList.add('invisible', 'opacity-0');
        menu.setAttribute('aria-hidden', 'true');
      }, 500);
    }

    // Expose closeMenu globally for inline onclick handlers
    window.closeMenu = closeMenu;

    toggle.addEventListener('click', () => state.menuOpen ? closeMenu() : openMenu());
    if (backdrop) backdrop.addEventListener('click', closeMenu);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.menuOpen) closeMenu();
    });
  }

  /* ================================================
     2. SCROLL DETECTION -- Header shadow,
        bottom-nav visibility, floating cart bar
     ================================================ */
  function initScroll() {
    const header = $('#SiteHeader');
    const bottomNav = $('#bottom-nav');
    const floatingBar = $('#floating-cart-bar');
    let ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const goingUp = y < state.lastScrollY || y < 80;

        // Header shadow on scroll
        if (header) {
          header.classList.toggle('shadow-lg', y > 20);
          header.classList.toggle('shadow-black/20', y > 20);
        }

        // Bottom nav: hide on scroll down, show on scroll up
        // Tailwind v4: -translate-x-1/2 uses CSS 'translate' property.
        // We use only 'transform: translateY()' to avoid doubling the X offset.
        if (bottomNav) {
          if (goingUp) {
            bottomNav.style.transform = '';
            bottomNav.style.opacity = '';
          } else if (y > 100) {
            bottomNav.style.transform = 'translateY(120%)';
            bottomNav.style.opacity = '0';
          }
        }

        // Floating cart bar: hide on scroll down (slides up behind header)
        if (floatingBar && state.cartCount > 0) {
          if (goingUp) {
            floatingBar.style.transform = '';
            floatingBar.style.opacity = '';
          } else if (y > 100) {
            floatingBar.style.transform = 'translateY(-120%)';
            floatingBar.style.opacity = '0';
          }
        }

        state.lastScrollY = y;
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ================================================
     3. BACK BUTTON -- Show on non-home pages
     ================================================ */
  function initBackButton() {
    const btn = $('#header-back');
    const spacer = $('#header-spacer');
    if (!btn) return;

    const isHome = window.location.pathname === '/' || window.location.pathname === '';

    if (!isHome) {
      btn.classList.remove('hidden');
      if (spacer) spacer.classList.remove('hidden');
    }

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (document.referrer && document.referrer.includes(window.location.host)) {
        history.back();
      } else {
        window.location.href = '/';
      }
    });
  }

  /* ================================================
     4. FIND-KIT MODAL -- Brand selector
     ================================================ */
  function initFindKit() {
    const openBtn = $('#open-findkit-btn');
    const modal = $('#findkit-modal');
    if (!modal) return;

    const closeBtn = $('#findkit-close');
    const backdrop = $('#findkit-backdrop');
    const searchInput = $('#findkit-search');
    const brandList = $('#findkit-brand-list');
    const emptyState = $('#findkit-empty');
    const dataScript = $('#findkit-data');

    let data = { brands: [], models: [] };
    if (dataScript) {
      try { data = JSON.parse(dataScript.textContent); } catch (e) { /* noop */ }
    }
    // Marche in ordine alfabetico (titolare 07/10); "Bmw" del metafield scritto BMW come nel catalogo
    const brandLabel = (name) => (String(name || '').toLowerCase() === 'bmw' ? 'BMW' : name);
    data.brands = (data.brands || []).map(b => ({ ...b, name: brandLabel(b.name) }))
      .sort((x, y) => String(x.name).localeCompare(String(y.name), 'it', { sensitivity: 'base' }));
    data.models = (data.models || []).map(m => ({ ...m, brand: brandLabel(m.brand) }));

    function openModal() {
      modal.classList.remove('invisible', 'opacity-0');
      modal.classList.add('opacity-100');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (searchInput) searchInput.value = '';
      renderBrands('');
    }

    function closeModal() {
      modal.classList.remove('opacity-100');
      modal.classList.add('opacity-0');
      setTimeout(() => {
        modal.classList.add('invisible');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }, 300);
    }

    function normalizeSearch(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    }

    function escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    function renderBrands(filter) {
      if (!brandList) return;
      const q = normalizeSearch(filter);
      const filtered = q
        ? data.brands.filter(b => normalizeSearch(b.name).includes(q))
        : data.brands;
      const models = q
        ? (data.models || []).filter(model => (
          normalizeSearch(model.title).includes(q)
          || normalizeSearch(model.brand).includes(q)
          || normalizeSearch(`${model.brand} ${model.title}`).includes(q)
        )).slice(0, 20)
        : [];

      if (filtered.length === 0 && models.length === 0 && q) {
        brandList.innerHTML = '';
        if (emptyState) emptyState.classList.remove('hidden');
        return;
      }
      if (emptyState) emptyState.classList.add('hidden');

      const brandRows = filtered.map(b => `
        <button class="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 transition-colors flex items-center gap-3 findkit-brand-item" data-brand-slug="${b.slug}">
          <span class="text-white/80 font-medium">${escapeHtml(b.name)}</span>
          <span class="ml-auto text-white/30 text-xs">${b.count || ''}</span>
        </button>
      `).join('');
      const modelRows = models.map(model => `
        <button class="w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 transition-colors flex items-center gap-3 findkit-model-item" data-model-handle="${escapeHtml(model.handle)}">
          <span>
            <span class="block text-white/80 font-medium">${escapeHtml(model.title)}</span>
            <span class="block text-white/35 text-xs">${escapeHtml(model.brand)}</span>
          </span>
          <span class="ml-auto text-white/30 text-xs">${model.productCount ? `${model.productCount} ${model.productCount === 1 ? 'prodotto' : 'prodotti'}` : ''}</span>
        </button>
      `).join('');
      brandList.innerHTML = q ? `${brandRows}${modelRows}` : brandRows;

      // Bind brand clicks: navigate to catalog page filtered by brand
      $$('.findkit-brand-item', brandList).forEach(el => {
        el.addEventListener('click', () => {
          const slug = el.dataset.brandSlug;
          closeModal();
          window.location.href = '/collections?brand=' + encodeURIComponent(slug);
        });
      });
      $$('.findkit-model-item', brandList).forEach(el => {
        el.addEventListener('click', () => {
          const handle = el.dataset.modelHandle;
          closeModal();
          window.location.href = '/collections/' + encodeURIComponent(handle);
        });
      });
    }

    // Event bindings
    if (openBtn) openBtn.addEventListener('click', openModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);
    if (searchInput) searchInput.addEventListener('input', () => renderBrands(searchInput.value));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('invisible')) closeModal();
    });

    // Expose for use by other modules (e.g. catalog page brand button)
    window.openFindkitModal = openModal;
  }

  /* ================================================
     5. CATALOG -- Brand filter (delegates to FindKit modal)
     ================================================ */
  function initCatalog() {
    const brandBtn = $('#catalog-brand-btn');
    const grid = $('#catalog-models-grid');
    const emptyState = $('#catalog-empty');
    if (!grid) return;

    const cards = $$('.catalog-model-card', grid);

    // Check URL param for pre-filtering (from FindKit modal or direct link)
    const urlParams = new URLSearchParams(window.location.search);
    const activeBrand = urlParams.get('brand') || '';

    // La ricerca \u00e8 solo nel modale "Trova il tuo kit" (titolare 07/10): qui resta il filtro per marca scelto nel modale
    function filterCards() {
      let visible = 0;
      cards.forEach(card => {
        const match = !activeBrand || card.dataset.brand === activeBrand;
        card.style.display = match ? '' : 'none';
        if (match) visible++;
      });
      // Titoletti delle marche: nascosti quando la marca non ha modelli visibili (filtro per marca)
      $$('.catalog-brand-group', grid).forEach(group => {
        const shown = $$('.catalog-model-card', group).some(card => card.style.display !== 'none');
        group.style.display = shown ? '' : 'none';
      });
      if (emptyState) emptyState.classList.toggle('hidden', visible !== 0);

      // Update button label
      const label = $('#catalog-brand-label');
      const clearBtn = $('#catalog-brand-clear');
      if (label) {
        label.textContent = activeBrand
          ? (activeBrand === 'bmw' ? 'BMW' : activeBrand.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
          : 'Seleziona il tuo marchio';
        label.classList.toggle('text-white/50', !activeBrand);
        label.classList.toggle('text-white', !!activeBrand);
      }
      if (clearBtn) clearBtn.style.display = activeBrand ? 'flex' : 'none';
    }

    // Brand button → open FindKit modal (same UX as home)
    if (brandBtn) {
      brandBtn.addEventListener('click', () => {
        if (window.openFindkitModal) window.openFindkitModal();
      });
    }

    // Clear button → remove brand filter via URL navigation
    const clearBtn = $('#catalog-brand-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        window.location.href = '/collections';
      });
    }

    // Apply initial brand filter (from URL param)
    filterCards();
  }

  /* ================================================
     6. IMAGE GALLERY -- Product card galleries
     ================================================ */
  function initGalleries() {
    $$('.product-gallery').forEach(gallery => {
      const images = $$('[data-gallery-img]', gallery);
      const counter = $('[data-counter-current]', gallery);
      if (images.length <= 1) return;

      let current = 0;
      const productId = gallery.dataset.productId;
      const thumbs = $$(`[data-product-id="${productId}"].product-thumbnails [data-thumb-idx]`);

      function showImage(idx) {
        if (idx < 0 || idx >= images.length) return;
        images[current].classList.remove('opacity-100');
        images[current].classList.add('opacity-0');
        images[idx].classList.remove('opacity-0');
        images[idx].classList.add('opacity-100');
        current = idx;
        if (counter) counter.textContent = idx + 1;

        // Update thumbnails
        thumbs.forEach((t, i) => {
          if (i === idx) {
            t.classList.add('ring-2', 'ring-accent', 'ring-offset-1', 'ring-offset-[#111]', 'scale-105');
            t.classList.remove('opacity-50');
          } else {
            t.classList.remove('ring-2', 'ring-accent', 'ring-offset-1', 'ring-offset-[#111]', 'scale-105');
            t.classList.add('opacity-50');
          }
        });
      }

      // Thumbnail clicks
      thumbs.forEach(t => {
        t.addEventListener('click', () => showImage(parseInt(t.dataset.thumbIdx, 10)));
      });

      // Swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      gallery.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      gallery.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0 && current < images.length - 1) showImage(current + 1);
          else if (diff < 0 && current > 0) showImage(current - 1);
        }
      }, { passive: true });

      // Click to open lightbox at current index
      gallery.addEventListener('click', (e) => {
        // Se il click è su un thumb, non aprire il lightbox
        if (e.target.closest('[data-thumb-idx]')) return;
        // Se il click è sul bottone Ingrandisci, non duplicare
        if (e.target.closest('.gallery-expand-btn')) return;
        const allUrls = gallery.dataset.images ? JSON.parse(gallery.dataset.images) : images.map(img => img.src);
        openLightbox(allUrls, current);
      });

      // Bottone Ingrandisci
      const expandBtn = gallery.querySelector('.gallery-expand-btn');
      if (expandBtn) {
        expandBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const allUrls = gallery.dataset.images ? JSON.parse(gallery.dataset.images) : images.map(img => img.src);
          openLightbox(allUrls, current);
        });
      }
    });
  }

  /* ================================================
     6a. SCHEDA TECNICA (modale nella card prodotto)
     Il contenuto è nel codice della pagina (indicizzabile); il modale si sposta nel body all'apertura
     così il position: fixed non dipende dai contenitori della card.
     ================================================ */
  function initSchedaTecnica() {
    let open = null;

    function close() {
      if (!open) return;
      open.classList.add('hidden');
      open.classList.remove('flex');
      document.body.style.overflow = '';
      open._opener?.focus();
      open = null;
    }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-scheda-open]');
      if (opener) {
        const modal = document.getElementById(opener.dataset.schedaOpen);
        if (!modal) return;
        if (modal.parentElement !== document.body) document.body.appendChild(modal);
        modal._opener = opener;
        modal.classList.remove('hidden');
        modal.classList.add('flex');
        document.body.style.overflow = 'hidden';
        open = modal;
        modal.querySelector('button[data-scheda-close]')?.focus();
        return;
      }
      if (open && e.target.closest('[data-scheda-close]')) close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  /* ================================================
     6b. LIGHTBOX GLOBALE
     ================================================ */
  let _lbImages = [];
  let _lbIdx = 0;

  function openLightbox(images, startIdx = 0) {
    if (!images || images.length === 0) return;
    _lbImages = images;
    _lbIdx = startIdx;
    const lb = $('#img-lightbox');
    if (!lb) return;
    updateLightboxImage();
    lb.classList.remove('hidden');
    lb.classList.add('flex');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    const lb = $('#img-lightbox');
    if (!lb) return;
    lb.classList.add('hidden');
    lb.classList.remove('flex');
    document.body.style.overflow = '';
  }

  function updateLightboxImage() {
    const img = $('#lightbox-img');
    const counter = $('#lightbox-counter');
    if (img) img.src = _lbImages[_lbIdx] || '';
    if (counter) counter.textContent = `${_lbIdx + 1} / ${_lbImages.length}`;
  }

  function initLightbox() {
    const lb = $('#img-lightbox');
    if (!lb) return;

    $('#lightbox-close')?.addEventListener('click', closeLightbox);
    $('#lightbox-prev')?.addEventListener('click', () => {
      _lbIdx = _lbIdx > 0 ? _lbIdx - 1 : _lbImages.length - 1;
      updateLightboxImage();
    });
    $('#lightbox-next')?.addEventListener('click', () => {
      _lbIdx = _lbIdx < _lbImages.length - 1 ? _lbIdx + 1 : 0;
      updateLightboxImage();
    });

    // Chiudi cliccando fuori dall'immagine
    lb.addEventListener('click', (e) => {
      if (e.target === lb) closeLightbox();
    });

    // Tastiera
    document.addEventListener('keydown', (e) => {
      if (lb.classList.contains('hidden')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') { _lbIdx = _lbIdx > 0 ? _lbIdx - 1 : _lbImages.length - 1; updateLightboxImage(); }
      if (e.key === 'ArrowRight') { _lbIdx = _lbIdx < _lbImages.length - 1 ? _lbIdx + 1 : 0; updateLightboxImage(); }
    });

    // Swipe sul lightbox
    let lbTouchStartX = 0;
    lb.addEventListener('touchstart', (e) => { lbTouchStartX = e.changedTouches[0].screenX; }, { passive: true });
    lb.addEventListener('touchend', (e) => {
      const diff = lbTouchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) { _lbIdx = _lbIdx < _lbImages.length - 1 ? _lbIdx + 1 : 0; }
        else { _lbIdx = _lbIdx > 0 ? _lbIdx - 1 : _lbImages.length - 1; }
        updateLightboxImage();
      }
    }, { passive: true });
  }

  // Esponi globalmente per uso da lightbox
  window.openLightbox = openLightbox;

  /* ================================================
     6c. NOTIFY FORMS (disponibilità prodotto)
     ================================================ */
  function initNotifyForms() {
    $$('.notify-form').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const successEl = form.querySelector('.notify-success');
        const modelHandle = form.dataset.model || '';
        const modelTitle = form.dataset.modelTitle || modelHandle;
        const firstName = form.querySelector('[name="firstName"]')?.value?.trim() || '';
        const email = form.querySelector('[name="email"]')?.value?.trim() || '';

        if (!email || !modelHandle) return;
        if (btn) btn.disabled = true;

        try {
          const apiUrl = (window.StylishCar?.checkoutApiUrl || '').replace(/\/$/, '');
          const res = await fetch(apiUrl + '/notify/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firstName, email, modelHandle, modelTitle }),
          });
          if (!res.ok) throw new Error('Errore server');
          if (successEl) {
            successEl.classList.remove('hidden');
            form.reset();
          }
        } catch (err) {
          if (btn) btn.disabled = false;
          const errEl = form.querySelector('.notify-error');
          if (errEl) { errEl.textContent = 'Si è verificato un errore, riprova.'; errEl.classList.remove('hidden'); }
        }
      });
    });
  }

  /* ================================================
     7. AJAX CART -- Add to cart, update badge,
        button state, cart page qty/remove
     ================================================ */

  // Forza la comparsa del bottom nav dopo aggiunta al carrello
  function revealBottomNav() {
    const bottomNav = $('#bottom-nav');
    if (!bottomNav) return;
    bottomNav.style.transform = '';
    bottomNav.style.opacity = '';
  }

  function initCart() {
    // Add to cart forms (on product / collection pages)
    $$('.add-to-cart-form').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('.add-to-cart-btn');
        const textEl = form.querySelector('.add-to-cart-text');
        const successEl = form.querySelector('.add-to-cart-success');
        if (!btn || btn.disabled) return;

        // Check if this product type has accessories available
        const productType = form.dataset.productType || '';
        const accData = getAccessoryData();
        const accessories = getAccessoriesForProductType(productType, accData);

        if (accessories && accessories.length > 0 && $('#accessory-modal')) {
          // Open accessory modal instead of adding directly
          openAccessoryModal(form);
          return;
        }

        // No accessories -- add directly
        await addToCartDirect(form, btn, textEl, successEl);
      });
    });

    // Cart page -- quantity buttons
    $$('.cart-qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.line, 10);
        const action = btn.dataset.action;
        const qtyEl = $(`.cart-qty-value[data-line="${line}"]`);
        if (!qtyEl) return;

        let qty = parseInt(qtyEl.textContent, 10);
        qty = action === 'plus' ? qty + 1 : Math.max(0, qty - 1);

        btn.disabled = true;
        try {
          await updateCartLine(line, qty);
        } catch (err) {
          showCartError('Non è stato possibile aggiornare la quantità. Riprova.');
          btn.disabled = false;
        }
      });
    });

    // Cart page -- remove buttons
    $$('.cart-remove-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.line, 10);
        btn.disabled = true;
        hideCartError();

        // Fetch current cart to check for bundle relationships
        try {
          const cartRes = await fetch('/cart.js');
          if (!cartRes.ok) throw new Error('Cart read failed');
          const cartData = await cartRes.json();
          const item = cartData.items[line - 1]; // line is 1-indexed

          if (item) {
            const bundleId = item.properties?._bundle_id;
            const isAccessory = !!item.properties?.['Accessorio per'];

            // If removing a PARENT product (has bundle_id but is NOT an accessory)
            if (bundleId && !isAccessory) {
              // Find ALL items with this bundle_id (parent + all accessories)
              const bundleLines = [];
              cartData.items.forEach((cartItem, idx) => {
                if (cartItem.properties?._bundle_id === bundleId) {
                  bundleLines.push(idx + 1); // 1-indexed
                }
              });

              // Sort descending to avoid index shift when removing
              bundleLines.sort((a, b) => b - a);
              for (const l of bundleLines) {
                await updateCartLine(l, 0, false);
              }
              window.location.reload();
              return;
            }
          }

          // Default: remove single item
          await updateCartLine(line, 0);
        } catch (err) {
          console.error('Cart remove failed:', err);
          showCartError('Non è stato possibile rimuovere il prodotto. Riprova.');
          btn.disabled = false;
        }
      });
    });

    // WhatsApp verification link builder (cart page)
    buildWaVerifyLink();

    // Payment method selection + checkout via backend
    initPaymentCheckout();
  }

  function showCartError(message) {
    const error = $('#checkout-error');
    if (!error) return;
    error.textContent = message;
    error.classList.remove('hidden');
    error.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideCartError() {
    const error = $('#checkout-error');
    if (error) error.classList.add('hidden');
  }

  async function updateCartLine(line, qty, reload = true) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty }),
    });
    if (!res.ok) {
      throw new Error(`Cart update HTTP ${res.status}`);
    }
    if (reload) {
      window.location.reload();
    }
  }

  function updateCartBadges() {
    const count = state.cartCount;
    // Bottom nav badge
    const badge = $('#cart-badge');
    if (badge) {
      // Update ONLY the count text inside the innermost span, not the entire badge
      const countSpan = badge.querySelector('.relative > .relative');
      if (countSpan) {
        countSpan.textContent = count > 9 ? '9+' : count;
      }
      if (count > 0) {
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }

    // Floating cart bar
    updateFloatingCartBar();
  }

  function buildWaVerifyLink() {
    const link = $('#wa-verify-link');
    if (!link) return;

    const waClean = (window.StylishCar?.waPhone || '393792707402').replace(/[^0-9]/g, '');
    const items = $$('.cart-line-item');
    const lines = items.map(el => {
      const name = el.querySelector('h3')?.textContent?.trim() || 'Prodotto';
      const qty = el.querySelector('.cart-qty-value')?.textContent?.trim() || '1';
      return `- ${name} (x${qty})`;
    });

    const total = $('#cart-total')?.textContent?.trim() || '';
    const msg = `Ciao! Vorrei verificare la compatibilit\u00E0 dei seguenti prodotti:\n\n${lines.join('\n')}\n\nTotale: ${total}`;

    link.href = `https://wa.me/${waClean}?text=${encodeURIComponent(msg)}`;
  }

  /* ================================================
     PAYMENT METHOD SELECTION + CHECKOUT PROXY
     ================================================ */
  function initPaymentCheckout() {
    const paymentInputs = $$('[name="payment_method"]');
    const surchargeRow = $('#payment-surcharge-row');
    const surchargeLabel = $('#payment-surcharge-label');
    const surchargeAmount = $('#payment-surcharge-amount');
    const checkoutBtn = $('#checkout-btn');
    const checkoutBtnTotal = $('#checkout-btn-total');

    if (!checkoutBtn || paymentInputs.length === 0) return;

    const SURCHARGES = {
      contrassegno: { type: 'fixed', amount: 20.00, label: 'Spedizione con Contrassegno' },
    };

    // Cart total in euros (reads raw cents from data attribute, set in Liquid)
    function getCartTotalEuros() {
      const totalEl = $('#cart-total');
      const cents = parseInt(totalEl?.dataset?.cents, 10);
      return cents ? cents / 100 : 0;
    }

    function formatEur(amount) {
      return '\u20AC' + amount.toFixed(2).replace('.', ',');
    }

    function updateSurchargeDisplay() {
      const selected = paymentInputs.find(r => r.checked)?.value || 'cards';
      const config = SURCHARGES[selected];
      const cartTotal = getCartTotalEuros();

      if (!config) {
        // Nessun costo aggiuntivo — nascondi la riga
        if (surchargeRow) {
          surchargeRow.classList.add('hidden');
          surchargeRow.style.display = '';
        }
        if (checkoutBtnTotal) checkoutBtnTotal.textContent = '\u2014 ' + formatEur(cartTotal);
        return;
      }

      let fee = 0;
      if (config.type === 'percent') {
        fee = Math.round(cartTotal * config.rate * 100) / 100;
      } else {
        fee = config.amount;
      }

      if (surchargeRow) {
        surchargeRow.classList.remove('hidden');
        surchargeRow.style.display = 'flex';
      }
      if (surchargeLabel) surchargeLabel.textContent = config.label;
      if (surchargeAmount) surchargeAmount.textContent = '+' + formatEur(fee);
      if (checkoutBtnTotal) checkoutBtnTotal.textContent = '\u2014 ' + formatEur(cartTotal + fee);
    }

    // Pagati nel checkout Shopify (il metodo lo sceglie il cliente lì); bonifico e contrassegno passano dalla pagina custom
    const NATIVE_METHODS = ['cards', 'klarna', 'satispay'];

    // Pagamento rateale (Klarna) solo da 300 € (titolare 12/10): sotto la soglia la riga resta visibile e apre la finestra
    const klarnaInput = $('#pm-klarna');
    const klarnaMinCents = parseInt(klarnaInput?.dataset?.minCents, 10) || 30000;
    function klarnaBlocked() {
      const cents = parseInt($('#cart-total')?.dataset?.cents, 10) || 0;
      return !!klarnaInput && cents < klarnaMinCents;
    }
    function openKlarnaMin() {
      $('#klarna-min-open')?.click();
      const modal = document.getElementById('klarna-min-modal');
      if (modal) modal._opener = klarnaInput;
    }
    let lastAllowed = paymentInputs.find(r => r.checked)?.value || 'cards';

    if (klarnaInput) {
      // Click (o tocco) sulla riga: annullare il click lascia selezionato il metodo di prima
      klarnaInput.addEventListener('click', (e) => {
        if (!klarnaBlocked()) return;
        e.preventDefault();
        openKlarnaMin();
      });
    }
    $('#klarna-min-single')?.addEventListener('click', () => {
      const cards = $('#pm-cards');
      if (!cards) return;
      cards.checked = true;
      cards.dispatchEvent(new Event('change', { bubbles: true }));
    });

    paymentInputs.forEach(input => {
      input.addEventListener('change', () => {
        // Da tastiera (frecce) la scelta arriva senza click: stessa regola del minimo
        if (input.value === 'klarna' && klarnaBlocked()) {
          const previous = paymentInputs.find(r => r.value === lastAllowed);
          if (previous) previous.checked = true;
          openKlarnaMin();
        } else {
          lastAllowed = input.value;
        }
        updateSurchargeDisplay();
      });
    });
    updateSurchargeDisplay();

    // Checkout button: checkout Shopify per carta, Klarna e Satispay; pagina custom per bonifico e contrassegno
    async function trackNativeBeginCheckout(method = 'cards') {
      if (typeof gtag === 'undefined') return;
      try {
        const response = await fetch('/cart.js', { cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        const checkoutKey = `sc_begin_checkout:${cart.token || 'cart'}:${method}`;
        if (sessionStorage.getItem(checkoutKey) === '1') return;
        await new Promise(resolve => {
          const timeout = setTimeout(resolve, 1600);
          gtag('event', 'begin_checkout', {
            currency: 'EUR',
            value: (cart.total_price || 0) / 100,
            payment_type: method,
            items: (cart.items || []).map(item => ({
              item_id: String(item.variant_id || ''),
              item_name: item.title || '',
              item_brand: item.properties?._brand || '',
              item_category: item.properties?._model || item.product_type || '',
              item_variant: item.variant_title || '',
              price: (item.price || 0) / 100,
              quantity: item.quantity || 1,
            })),
            event_timeout: 1500,
            event_callback: () => {
              clearTimeout(timeout);
              resolve();
            },
          });
        });
        sessionStorage.setItem(checkoutKey, '1');
      } catch (_e) {}
    }

    checkoutBtn.addEventListener('click', async () => {
      const selected = paymentInputs.find(r => r.checked)?.value || 'cards';
      checkoutBtn.disabled = true;

      if (selected === 'klarna' && klarnaBlocked()) {
        checkoutBtn.disabled = false;
        openKlarnaMin();
        return;
      }
      if (NATIVE_METHODS.includes(selected)) {
        // Carta, Klarna, Satispay: checkout Shopify nativo, dove il cliente conferma il metodo
        await trackNativeBeginCheckout(selected);
        window.location.href = '/checkout';
      } else {
        // Tutti gli altri: pagina checkout custom con il metodo come parametro
        window.location.href = '/pages/pagamento?method=' + encodeURIComponent(selected);
      }
    });
  }

  /* --- Cart helpers ------------------------------- */

  /** Count only base products (exclude accessories) from cart data */
  function countBaseProducts(cartData) {
    if (!cartData?.items) return cartData?.item_count || 0;
    return cartData.items.filter(item => !item.properties?.['Accessorio per']).reduce((sum, item) => sum + item.quantity, 0);
  }

  function showAddToCartError(form, message) {
    if (!form) return;
    let error = form.querySelector('.add-to-cart-error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'add-to-cart-error mt-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs text-center';
      form.appendChild(error);
    }
    error.textContent = message;
  }

  async function addToCartDirect(form, btn, textEl, successEl) {
    btn.disabled = true;
    btn.classList.add('opacity-70');
    const previousError = form.querySelector('.add-to-cart-error');
    if (previousError) previousError.remove();

    const variantId = form.querySelector('[name="id"]').value;
    const qty = parseInt(form.querySelector('[name="quantity"]').value, 10) || 1;
    const model = form.dataset.collection || '';
    const brand = form.dataset.brand || '';
    const properties = {};
    if (model) properties._model = model;
    if (brand) properties._brand = brand;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: qty,
          ...(Object.keys(properties).length ? { properties } : {}),
        }),
      });

      if (!res.ok) throw new Error('Cart add failed');

      const cartRes = await fetch('/cart.js');
      const cartData = await cartRes.json();
      state.cartCount = countBaseProducts(cartData);
      state.cartTotal = cartData.total_price || 0;
      updateCartBadges();
      revealBottomNav();

      // Analytics: AddToCart
      try {
        const productTitle = btn ? (btn.closest('form')?.dataset?.productName || '') : '';
        const productPrice = btn ? parseFloat(btn.closest('form')?.dataset?.productPrice || 0) / 100 : 0;
        if (typeof gtag !== 'undefined') {
          gtag('event', 'add_to_cart', { currency: 'EUR', value: productPrice, items: [{ item_id: String(variantId), item_name: productTitle, price: productPrice, quantity: qty }] });
        }
        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', { value: productPrice, currency: 'EUR', content_ids: [String(variantId)], content_type: 'product' });
        }
      } catch (_e) {}

      if (textEl) { textEl.classList.add('hidden'); textEl.classList.remove('inline-flex'); }
      if (successEl) { successEl.classList.remove('hidden'); successEl.classList.add('inline-flex'); }
      btn.classList.remove('bg-accent', 'hover:bg-accent/90', 'shadow-accent/20');
      btn.classList.add('bg-green-600');

      setTimeout(() => {
        if (textEl) { textEl.classList.remove('hidden'); textEl.classList.add('inline-flex'); }
        if (successEl) { successEl.classList.add('hidden'); successEl.classList.remove('inline-flex'); }
        btn.classList.add('bg-accent', 'hover:bg-accent/90', 'shadow-accent/20');
        btn.classList.remove('bg-green-600');
        btn.disabled = false;
        btn.classList.remove('opacity-70');
      }, 2000);
    } catch (err) {
      console.error('Add to cart error:', err);
      showAddToCartError(form, 'Non è stato possibile aggiungere il prodotto. Riprova.');
      btn.disabled = false;
      btn.classList.remove('opacity-70');
    }
  }

  async function addToCartWithAccessories(items) {
    const error = $('#acc-add-error');
    if (error) error.classList.add('hidden');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error('Cart add failed');

      const cartRes = await fetch('/cart.js');
      const cartData = await cartRes.json();
      state.cartCount = countBaseProducts(cartData);
      state.cartTotal = cartData.total_price || 0;
      updateCartBadges();
      revealBottomNav();

      // Analytics: AddToCart (solo prodotto base, non accessori)
      try {
        const baseItem = items[0];
        if (baseItem && typeof gtag !== 'undefined') {
          gtag('event', 'add_to_cart', { currency: 'EUR', items: [{ item_id: String(baseItem.id), quantity: baseItem.quantity || 1 }] });
        }
        if (baseItem && typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', { currency: 'EUR', content_ids: [String(baseItem.id)], content_type: 'product' });
        }
      } catch (_e) {}

      return true;
    } catch (err) {
      console.error('Add to cart with accessories error:', err);
      if (error) {
        error.textContent = 'Non è stato possibile aggiungere il prodotto. Riprova.';
        error.classList.remove('hidden');
      }
      return false;
    }
  }

  /* ================================================
     7b. ACCESSORY MODAL -- Upsell bordi/flap
     ================================================ */
  let accState = {
    form: null,
    bordiSelected: false,
    bordiVariantId: null,
    flapSelected: false,
    basePrice: 0,
  };

  function getAccessoryData() {
    const script = $('#accessory-data');
    if (!script) return null;
    try {
      return normalizeAccessoryData(JSON.parse(script.textContent));
    } catch (e) {
      return normalizeAccessoryData({
        bordi: {},
        flap: {},
        categoryMap: {
          'Lame Anteriori': ['bordi', 'flap'],
          'Lame Laterali': ['bordi', 'flap'],
          'Diffusori Posteriori': ['bordi'],
          'Spoiler': ['bordi'],
          'Altro': [],
        },
      });
    }
  }

  function normalizeAccessoryData(data) {
    if (!data) return null;

    const fallbackBordiVariants = [
      { id: 55563844649300, title: 'Rosso Racing', color: '#FF0100', price: 2000, available: true },
      { id: 55563844682068, title: 'Electric Blue', color: '#0000FF', price: 2000, available: true },
      { id: 55563844714836, title: 'Giallo', color: '#FFFF00', price: 2000, available: true },
      { id: 57348949049684, title: 'Giallo Oro', color: '#FFD701', price: 2000, available: true },
      { id: 57348949082452, title: 'Arancione', color: '#FE8D00', price: 2000, available: true },
      { id: 55563844780372, title: 'Fucsia', color: '#FF1493', price: 2000, available: true },
      { id: 57348949115220, title: 'Verde', color: '#29B929', price: 2000, available: true },
      { id: 57348949147988, title: 'Verde Lime', color: '#CCFF01', price: 2000, available: true },
      { id: 57348949180756, title: 'Verde Fluo', color: '#39FF14', price: 2000, available: true },
      { id: 57348949213524, title: 'Verde Acqua', color: '#00A186', price: 2000, available: true },
      { id: 57348949246292, title: 'Grigio', color: '#C0C0C0', price: 2000, available: true },
      { id: 57348949279060, title: 'Bianco', color: '#FFFFFF', price: 2000, available: true },
    ];

    data.bordi = data.bordi || {};
    data.flap = data.flap || {};

    const bordiPrice = Number(data.bordi.price);
    const flapPrice = Number(data.flap.price);
    data.bordi.price = Number.isFinite(bordiPrice) && bordiPrice > 0 ? bordiPrice : Number(data.bordi.fallbackPrice || 2000);
    data.flap.price = Number.isFinite(flapPrice) && flapPrice > 0 ? flapPrice : Number(data.flap.fallbackPrice || 4000);

    if (!Array.isArray(data.bordi.variants) || data.bordi.variants.length === 0) {
      data.bordi.variants = Array.isArray(data.bordi.fallbackVariants) && data.bordi.fallbackVariants.length
        ? data.bordi.fallbackVariants
        : fallbackBordiVariants;
    }
    data.bordi.available = data.bordi.variants.some(v => v && v.id && v.available !== false);

    data.flap.variantId = data.flap.variantId || data.flap.fallbackVariantId || 55563845796180;
    data.flap.available = Boolean(data.flap.variantId);

    return data;
  }

  function renderBordiColorOptions(accData) {
    const grid = $('#acc-bordi-color-grid');
    if (!grid || !accData?.bordi?.variants) return;
    grid.innerHTML = '';

    accData.bordi.variants
      .filter(variant => variant && variant.id && variant.available !== false)
      .forEach(variant => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.variantId = String(variant.id);
        btn.dataset.colorName = variant.title || '';
        if (variant.color) {
          // Riquadro colore come sul sito precedente; il nome resta leggibile dagli screen reader.
          btn.className = 'acc-color-btn acc-swatch border-white/10';
          btn.style.backgroundColor = variant.color;
          btn.title = variant.title || '';
          btn.setAttribute('aria-label', variant.title || 'Colore');
          const label = document.createElement('span');
          label.className = 'acc-sr-only';
          label.textContent = variant.title || 'Colore';
          btn.appendChild(label);
        } else {
          btn.className = 'acc-color-btn px-3 py-1.5 rounded-lg border border-white/10 text-white/60 text-xs font-medium transition-all hover:border-white/30 hover:text-white/80';
          btn.textContent = variant.title || 'Colore';
        }
        btn.addEventListener('click', () => selectBordiColor(btn));
        grid.appendChild(btn);
      });
  }

  function getAccessoriesForProductType(productType, accData) {
    if (!accData) return [];
    const type = (productType || '').trim();
    if (accData.categoryMap?.[type]) return accData.categoryMap[type];

    const normalized = type.toLowerCase();
    if (normalized.includes('lama anteriore') || normalized.includes('lame anteriori') || normalized.includes('lame laterali')) return ['bordi', 'flap'];
    return ['bordi'];
  }

  function formatMoney(cents) {
    return '\u20AC' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function openAccessoryModal(form) {
    const modal = $('#accessory-modal');
    const panel = $('#accessory-panel');
    if (!modal || !panel) return;

    const accData = getAccessoryData();
    if (!accData) return;

    const productType = form.dataset.productType || '';
    const accessories = getAccessoriesForProductType(productType, accData);
    const addError = $('#acc-add-error');
    if (addError) addError.classList.add('hidden');

    // Reset state
    accState = {
      form: form,
      bordiSelected: false,
      bordiVariantId: null,
      flapSelected: false,
      basePrice: parseInt(form.dataset.productPrice, 10) || 0,
    };

    // Populate product info
    const nameEl = $('#acc-product-name');
    const typeEl = $('#acc-product-type-label');
    const priceEl = $('#acc-product-price');
    const imgEl = $('#acc-product-img');

    if (nameEl) nameEl.textContent = form.dataset.productName || '';
    if (typeEl) typeEl.textContent = productType;
    if (priceEl) priceEl.textContent = formatMoney(accState.basePrice);
    if (imgEl) imgEl.src = form.dataset.productImg || '';

    // Show/hide sections based on category
    const bordiSection = $('#acc-bordi-section');
    const flapSection = $('#acc-flap-section');
    if (bordiSection) bordiSection.classList.toggle('hidden', !accessories.includes('bordi'));
    if (flapSection) flapSection.classList.toggle('hidden', !accessories.includes('flap'));
    renderBordiColorOptions(accData);

    // Reset checkboxes
    resetAccessoryCheckbox('bordi');
    resetAccessoryCheckbox('flap');

    // Reset color selection
    $$('.acc-color-btn').forEach(btn => {
      btn.classList.remove('border-accent', 'text-accent', 'bg-accent/10');
      btn.classList.add('border-white/10', 'text-white/60');
    });
    setBordiColorLabel('');

    // Update prices
    updateAccessoryTotal();

    // Hide bottom nav while modal is open
    const bottomNav = $('#bottom-nav');
    if (bottomNav) bottomNav.style.display = 'none';

    // Show modal
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      panel.classList.remove('translate-y-full');
    });
  }

  function closeAccessoryModal() {
    const modal = $('#accessory-modal');
    const panel = $('#accessory-panel');
    if (!modal || !panel) return;

    panel.classList.add('translate-y-full');
    setTimeout(() => {
      modal.classList.add('hidden');
      document.body.style.overflow = '';
      accState.form = null;
      // Restore bottom nav
      const bottomNav = $('#bottom-nav');
      if (bottomNav) bottomNav.style.display = '';
    }, 300);
  }

  function resetAccessoryCheckbox(type) {
    const check = $(`#acc-${type}-check`);
    if (!check) return;
    check.classList.remove('bg-accent', 'border-accent');
    check.classList.add('border-white/20');
    check.querySelector('svg').classList.add('opacity-0');
    const colors = $(`#acc-${type}-colors`);
    if (colors) colors.classList.add('hidden');
  }

  function toggleAccessory(type) {
    const isSelected = type === 'bordi' ? accState.bordiSelected : accState.flapSelected;
    const newState = !isSelected;

    if (type === 'bordi') {
      accState.bordiSelected = newState;
      if (!newState) {
        accState.bordiVariantId = null;
        $$('.acc-color-btn').forEach(btn => {
          btn.classList.remove('border-accent', 'text-accent', 'bg-accent/10');
          btn.classList.add('border-white/10', 'text-white/60');
        });
        setBordiColorLabel('');
      }
    } else {
      accState.flapSelected = newState;
    }

    const check = $(`#acc-${type}-check`);
    if (check) {
      if (newState) {
        check.classList.add('bg-accent', 'border-accent');
        check.classList.remove('border-white/20');
        check.querySelector('svg').classList.remove('opacity-0');
      } else {
        check.classList.remove('bg-accent', 'border-accent');
        check.classList.add('border-white/20');
        check.querySelector('svg').classList.add('opacity-0');
      }
    }

    // Show/hide bordi color section
    if (type === 'bordi') {
      const colors = $('#acc-bordi-colors');
      if (colors) colors.classList.toggle('hidden', !newState);
    }

    updateAccessoryTotal();
  }

  function setBordiColorLabel(name) {
    const colorLabel = $('#acc-bordi-color-name');
    if (colorLabel) colorLabel.textContent = name ? `Colore scelto: ${name}` : '';
  }

  function selectBordiColor(btn) {
    $$('.acc-color-btn').forEach(b => {
      b.classList.remove('border-accent', 'text-accent', 'bg-accent/10');
      b.classList.add('border-white/10', 'text-white/60');
    });
    btn.classList.add('border-accent', 'text-accent', 'bg-accent/10');
    btn.classList.remove('border-white/10', 'text-white/60');
    accState.bordiVariantId = parseInt(btn.dataset.variantId, 10);
    setBordiColorLabel(btn.dataset.colorName || '');

    // Clear any validation error
    const errMsg = document.querySelector('.acc-color-error');
    if (errMsg) errMsg.remove();
    const colorsSection = $('#acc-bordi-colors');
    if (colorsSection) colorsSection.classList.remove('ring-2', 'ring-red-500/50', 'rounded-lg');
  }

  function updateAccessoryTotal() {
    const accData = getAccessoryData();
    if (!accData) return;

    let total = accState.basePrice;
    const bordiRow = $('#acc-bordi-price-row');
    const flapRow = $('#acc-flap-price-row');

    if (accState.bordiSelected) {
      total += accData.bordi.price;
      if (bordiRow) bordiRow.classList.remove('hidden');
    } else {
      if (bordiRow) bordiRow.classList.add('hidden');
    }

    if (accState.flapSelected) {
      total += accData.flap.price;
      if (flapRow) flapRow.classList.remove('hidden');
    } else {
      if (flapRow) flapRow.classList.add('hidden');
    }

    const basePriceEl = $('#acc-base-price');
    if (basePriceEl) basePriceEl.textContent = formatMoney(accState.basePrice);

    const totalEl = $('#acc-total-price');
    if (totalEl) totalEl.textContent = formatMoney(total);

    // Update confirm button text
    const confirmText = $('#acc-confirm-text');
    if (confirmText) {
      confirmText.textContent = `Aggiungi al Carrello \u2014 ${formatMoney(total)}`;
    }
  }

  async function confirmAccessoryAdd() {
    if (!accState.form) return;

    // Validate: if bordi selected, color must be chosen
    if (accState.bordiSelected && !accState.bordiVariantId) {
      const colorGrid = $('#acc-bordi-color-grid');
      if (colorGrid) {
        // Flash the color section to indicate required
        const colorsSection = $('#acc-bordi-colors');
        if (colorsSection) {
          colorsSection.classList.add('ring-2', 'ring-red-500/50', 'rounded-lg');
          // Add/update error message
          let errMsg = colorsSection.querySelector('.acc-color-error');
          if (!errMsg) {
            errMsg = document.createElement('p');
            errMsg.className = 'acc-color-error text-red-400 text-xs mt-2 font-medium';
            errMsg.textContent = 'Seleziona un colore per i bordi';
            colorsSection.appendChild(errMsg);
          }
          setTimeout(() => colorsSection.classList.remove('ring-2', 'ring-red-500/50', 'rounded-lg'), 2000);
        }
        // Scroll to the color section within the modal
        const panel = $('#accessory-panel');
        if (panel && colorsSection) colorsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const accData = getAccessoryData();
    const variantId = accState.form.querySelector('[name="id"]').value;
    const productName = accState.form.dataset.productName || '';
    const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

    const items = [];

    // Base product
    const baseItem = {
      id: parseInt(variantId, 10),
      quantity: 1,
    };
    const baseModel = accState.form.dataset.collection || '';
    const baseBrand = accState.form.dataset.brand || '';
    if (accState.bordiSelected || accState.flapSelected) {
      baseItem.properties = { _bundle_id: bundleId };
      if (baseModel) baseItem.properties._model = baseModel;
      if (baseBrand) baseItem.properties._brand = baseBrand;
    } else if (baseModel || baseBrand) {
      baseItem.properties = {};
      if (baseModel) baseItem.properties._model = baseModel;
      if (baseBrand) baseItem.properties._brand = baseBrand;
    }
    items.push(baseItem);

    // Bordi accessory
    if (accState.bordiSelected && accState.bordiVariantId && accData) {
      items.push({
        id: accState.bordiVariantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          'Accessorio per': productName,
        },
      });
    }

    // Flap accessory
    if (accState.flapSelected && accData?.flap?.variantId) {
      items.push({
        id: accData.flap.variantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          'Accessorio per': productName,
        },
      });
    }

    const confirmBtn = $('#acc-confirm-btn');
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.classList.add('opacity-70');
    }

    const success = await addToCartWithAccessories(items);

    if (success) {
      // Show success on original button
      const btn = accState.form.querySelector('.add-to-cart-btn');
      const textEl = accState.form.querySelector('.add-to-cart-text');
      const successEl = accState.form.querySelector('.add-to-cart-success');

      if (textEl) { textEl.classList.add('hidden'); textEl.classList.remove('inline-flex'); }
      if (successEl) { successEl.classList.remove('hidden'); successEl.classList.add('inline-flex'); }
      if (btn) {
        btn.classList.remove('bg-accent', 'hover:bg-accent/90', 'shadow-accent/20');
        btn.classList.add('bg-green-600');
        setTimeout(() => {
          if (textEl) { textEl.classList.remove('hidden'); textEl.classList.add('inline-flex'); }
          if (successEl) { successEl.classList.add('hidden'); successEl.classList.remove('inline-flex'); }
          btn.classList.add('bg-accent', 'hover:bg-accent/90', 'shadow-accent/20');
          btn.classList.remove('bg-green-600');
        }, 2000);
      }

      closeAccessoryModal();
    }

    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.classList.remove('opacity-70');
    }
  }

  function initAccessoryModal() {
    const modal = $('#accessory-modal');
    if (!modal) return;

    // Backdrop close
    const backdrop = $('#accessory-backdrop');
    if (backdrop) backdrop.addEventListener('click', closeAccessoryModal);

    const closeBtn = $('#acc-close-btn');
    if (closeBtn) closeBtn.addEventListener('click', closeAccessoryModal);

    // Bordi toggle
    const bordiToggle = $('#acc-bordi-toggle');
    if (bordiToggle) bordiToggle.addEventListener('click', () => toggleAccessory('bordi'));

    // Flap toggle
    const flapToggle = $('#acc-flap-toggle');
    if (flapToggle) flapToggle.addEventListener('click', () => toggleAccessory('flap'));

    // Bordi color buttons
    $$('.acc-color-btn').forEach(btn => {
      btn.addEventListener('click', () => selectBordiColor(btn));
    });

    // Confirm button
    const confirmBtn = $('#acc-confirm-btn');
    if (confirmBtn) confirmBtn.addEventListener('click', confirmAccessoryAdd);

    // Skip button -- add just the base product
    const skipBtn = $('#acc-skip-btn');
    if (skipBtn) {
      skipBtn.addEventListener('click', async () => {
        if (!accState.form) return;
        const btn = accState.form.querySelector('.add-to-cart-btn');
        const textEl = accState.form.querySelector('.add-to-cart-text');
        const successEl = accState.form.querySelector('.add-to-cart-success');
        closeAccessoryModal();
        if (btn) await addToCartDirect(accState.form, btn, textEl, successEl);
      });
    }

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) closeAccessoryModal();
    });
  }

  /* ================================================
     8. FLOATING CART BAR (non-cart pages)
     ================================================ */
  function initFloatingCartBar() {
    // Only show on non-cart pages
    const isCartPage = window.location.pathname.includes('/cart');
    if (isCartPage) {
      const bar = $('#floating-cart-bar');
      if (bar) bar.classList.add('opacity-0', '-translate-y-full', 'pointer-events-none');
      return;
    }

    // Fetch current cart to init state
    fetch('/cart.js')
      .then(r => r.json())
      .then(data => {
        state.cartCount = countBaseProducts(data);
        state.cartTotal = data.total_price || 0;
        updateCartBadges();
        updateFloatingCartBar();
      })
      .catch(() => {});
  }

  function updateFloatingCartBar() {
    const bar = $('#floating-cart-bar');
    if (!bar) return;

    const countEl = $('#fcb-count');
    const totalEl = $('#fcb-total');

    if (state.cartCount > 0) {
      bar.classList.remove('opacity-0', '-translate-y-full', 'pointer-events-none');
      if (countEl) {
        const word = state.cartCount === 1 ? 'articolo' : 'articoli';
        countEl.textContent = `${state.cartCount} ${word} nel carrello`;
      }
      if (totalEl) {
        totalEl.textContent = formatMoney(state.cartTotal);
      }
    } else {
      bar.classList.add('opacity-0', '-translate-y-full', 'pointer-events-none');
    }
    // X7 (titolare 08/10): il contenuto scende sotto la barra solo quando la barra c'è (mai nel carrello)
    const main = document.getElementById('MainContent');
    if (main) main.classList.toggle('has-cart-bar', state.cartCount > 0 && !window.location.pathname.includes('/cart'));
  }

  /* ================================================
     9. INTERSECTION OBSERVER -- Fade-in animations
     ================================================ */
  function initAnimations() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInUp');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    $$('[data-animate]').forEach(el => observer.observe(el));
  }

  /* ================================================
     PAGINA CHECKOUT CUSTOM (/pages/pagamento)
     ================================================ */
  function initCheckoutPage() {
    const page = $('#pagamento-page');
    if (!page) return; // non siamo sulla pagina pagamento

    const params = new URLSearchParams(window.location.search);
    const requestedMethod = params.get('method') || 'bonifico';
    const method = ['bonifico', 'contrassegno'].includes(requestedMethod) ? requestedMethod : 'bonifico';
    const apiUrl = (page.dataset.apiUrl || window.StylishCar?.checkoutApiUrl || '').replace(/\/$/, '');

    const SURCHARGES = {
      contrassegno: { type: 'fixed', amount: 20.00, label: 'Spedizione con Contrassegno' },
    };

    // Aggiorna label descrittiva metodo
    const pmLabelDesc = $('#pm-label-desc');
    const pmLabels = {
      bonifico: 'Inserisci i tuoi dati di consegna. Riceverai le istruzioni per il bonifico.',
      contrassegno: 'Inserisci i tuoi dati di consegna. Pagherai al corriere alla consegna.',
    };
    if (pmLabelDesc) pmLabelDesc.textContent = pmLabels[method] || pmLabels.bonifico;

    // Mostra sezione corretta
    ['#pm-bonifico', '#pm-contrassegno'].forEach(sel => {
      const el = $(sel);
      if (el) el.classList.add('hidden');
    });
    const pmSection = $(`#pm-${method}`);
    if (pmSection) pmSection.classList.remove('hidden');

    // Stato carrello (caricato una volta)
    let cartItems = [];
    let cartTotal = 0; // euro
    let shippingLine = { title: 'Express BRT', price: 0 };

    function toEur(n) {
      return (Math.round(n * 100) / 100).toFixed(2);
    }
    function formatEur(n) {
      return '\u20AC' + toEur(n).replace('.', ',');
    }

    // Carica carrello e popola riepilogo
    async function loadCart() {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Impossibile leggere il carrello');
      const data = await res.json();

      cartItems = data.items
        .filter(item => item.variant_id)
        .map(item => ({
          variantId: item.variant_id,
          quantity: item.quantity,
          price: toEur(item.price / 100),
          name: item.title,
          productType: item.product_type || '',
          variantTitle: item.variant_title || '',
          isAccessory: !!(item.properties && item.properties['Accessorio per']),
          model: (item.properties && item.properties._model) || '',
          brand: (item.properties && item.properties._brand) || '',
          properties: item.properties || {},
        }));
      if (cartItems.length === 0) {
        throw new Error('Il carrello è vuoto. Torna al catalogo e aggiungi almeno un prodotto.');
      }

      cartTotal = data.total_price / 100;
      const activeConfirm = method === 'contrassegno'
        ? $('#confirm-contrassegno-btn')
        : $('#confirm-bonifico-btn');
      if (activeConfirm) activeConfirm.disabled = false;

      // Riepilogo UI
      const summaryItems = $('#summary-items');
      if (summaryItems) {
        summaryItems.innerHTML = cartItems.map(item =>
          `<div class="flex justify-between">
            <span class="truncate max-w-[65%]">${item.name}</span>
            <span>${item.quantity > 1 ? `${item.quantity}\u00d7 ` : ''}${formatEur(parseFloat(item.price) * item.quantity)}</span>
          </div>`
        ).join('');
      }

      const surcharge = SURCHARGES[method];
      let fee = 0;
      if (surcharge) {
        fee = surcharge.type === 'percent'
          ? Math.round(cartTotal * surcharge.rate * 100) / 100
          : surcharge.amount;

        const surchargeRow = $('#summary-surcharge-row');
        if (surchargeRow) {
          surchargeRow.style.display = 'flex';
          surchargeRow.classList.remove('hidden');
        }
        const surchargeLabel = $('#summary-surcharge-label');
        const surchargeAmt = $('#summary-surcharge-amount');
        if (surchargeLabel) surchargeLabel.textContent = surcharge.label;
        if (surchargeAmt) surchargeAmt.textContent = '+' + formatEur(fee);
      }

      const shippingPrice = shippingLine.price || 0;
      const totalEur = cartTotal + fee + shippingPrice;
      const subtotalEl = $('#summary-subtotal');
      const totalEl = $('#summary-total');
      if (subtotalEl) subtotalEl.textContent = formatEur(cartTotal);
      if (totalEl) totalEl.textContent = formatEur(totalEur);

      // Imposta valori specifici per metodo
      const bonImporto = $('#bonifico-importo');
      if (bonImporto) bonImporto.textContent = formatEur(cartTotal + shippingPrice);
      const codTotal = $('#contrassegno-total');
      if (codTotal) codTotal.textContent = formatEur(totalEur);

      try {
        const checkoutKey = `sc_begin_checkout:${data.token || 'cart'}:${method}`;
        if (
          typeof gtag !== 'undefined'
          && sessionStorage.getItem(checkoutKey) !== '1'
        ) {
          gtag('event', 'begin_checkout', {
            currency: 'EUR',
            value: totalEur,
            payment_type: method,
            items: cartItems.map(item => ({
              item_id: String(item.variantId || ''),
              item_name: item.name || '',
              item_brand: item.brand || '',
              item_category: item.model || (item.isAccessory ? 'Accessorio' : ''),
              item_variant: item.variantTitle || '',
              price: parseFloat(item.price || 0),
              quantity: item.quantity || 1,
            })),
          });
          sessionStorage.setItem(checkoutKey, '1');
        }
      } catch (_e) {}

      return { cartTotal, fee, totalEur };
    }

    // Raccoglie dati dal form spedizione, valida, ritorna oggetto o null
    function getShippingData() {
      const form = $('#shipping-form');
      if (!form) return null;

      const data = {
        firstName: form.querySelector('[name="firstName"]')?.value?.trim(),
        lastName: form.querySelector('[name="lastName"]')?.value?.trim(),
        email: form.querySelector('[name="email"]')?.value?.trim(),
        phone: form.querySelector('[name="phone"]')?.value?.trim(),
        address1: form.querySelector('[name="address1"]')?.value?.trim(),
        address2: form.querySelector('[name="address2"]')?.value?.trim() || '',
        zip: form.querySelector('[name="zip"]')?.value?.trim(),
        city: form.querySelector('[name="city"]')?.value?.trim(),
        province: form.querySelector('[name="province"]')?.value?.trim().toUpperCase(),
        countryCode: 'IT',
      };

      const required = ['firstName', 'lastName', 'email', 'phone', 'address1', 'zip', 'city', 'province'];
      const missing = required.filter(k => !data[k]);
      if (missing.length > 0) {
        showError('Compila tutti i campi obbligatori: ' + missing.map(k => ({
          firstName: 'Nome', lastName: 'Cognome', email: 'Email', phone: 'Telefono',
          address1: 'Indirizzo', zip: 'CAP', city: 'Città', province: 'Provincia',
        }[k] || k)).join(', '));
        return null;
      }

      // Validazione email semplice
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        showError('Inserisci un indirizzo email valido.');
        return null;
      }
      if (!/^\d{5}$/.test(data.zip)) {
        showError('Inserisci un CAP italiano valido di 5 cifre.');
        return null;
      }
      if (!/^[A-Z]{2}$/.test(data.province)) {
        showError('Inserisci la sigla della provincia di 2 lettere.');
        return null;
      }
      const phoneDigits = data.phone.replace(/\D/g, '');
      if (phoneDigits.length < 8 || phoneDigits.length > 15) {
        showError('Inserisci un numero di telefono valido.');
        return null;
      }

      return data;
    }

    function showError(msg) {
      const el = $('#checkout-page-error');
      if (el) {
        el.textContent = msg;
        el.classList.remove('hidden');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    function hideError() {
      const el = $('#checkout-page-error');
      if (el) el.classList.add('hidden');
    }

    function showConfirmed(orderName, paymentMessage, isPaymentHtml = false, orderData = null) {
      // Nascondi tutto tranne confirmed
      ['#checkout-header', '#order-summary', '#shipping-method-select', '#shipping-form', `#pm-${method}`, '#checkout-page-error'].forEach(sel => {
        const el = $(sel);
        if (el) el.classList.add('hidden');
      });
      const confirmedEl = $('#order-confirmed');
      if (!confirmedEl) return;
      confirmedEl.classList.remove('hidden');

      // ── Helper locale ──
      function esc(s) { return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

      // ── Riepilogo prodotti ──
      const items = orderData?.items || [];
      const itemsHtml = items.map(item => {
        if (item.isAccessory) {
          const accLabel = [item.name, item.variantTitle].filter(Boolean).join(', ');
          return `<div class="py-1 border-b border-white/5 last:border-0">
            <p class="text-white/40 text-xs">- Accessorio: ${esc(accLabel)}</p>
          </div>`;
        }
        const lineTotal = (parseFloat(item.price) * item.quantity).toFixed(2).replace('.', ',');
        const brandModel = [item.brand, item.model].filter(Boolean).join(' ');
        return `<div class="py-2.5 border-b border-white/5 last:border-0">
          <div class="flex justify-between gap-2 items-start">
            <div>
              <p class="text-white text-sm font-medium">${esc(item.name)}${item.quantity > 1 ? ` <span class="text-white/40 font-normal text-xs">&times;${item.quantity}</span>` : ''}</p>
              ${brandModel ? `<p class="text-white/50 text-xs mt-0.5">${esc(brandModel)}</p>` : ''}
            </div>
            <span class="text-white text-sm shrink-0">&euro;${lineTotal}</span>
          </div>
        </div>`;
      }).join('');

      const shippingAmt = parseFloat(orderData?.shippingLine?.price || 0);
      const totalAmt = parseFloat(orderData?.total || 0);
      const subtotalAmt = shippingAmt > 0 ? (totalAmt - shippingAmt) : totalAmt;
      const shippingTitle = orderData?.shippingLine?.title || 'Spedizione';
      const email = esc(orderData?.email || '');
      const sh = orderData?.shipping || {};
      const deliveryName = esc([sh.firstName, sh.lastName].filter(Boolean).join(' '));
      const deliveryAddr = esc([sh.address1, sh.address2].filter(Boolean).join(', '));
      const deliveryCityLine = esc([sh.zip, sh.city, sh.province].filter(Boolean).join(' '));

      const summaryHtml = items.length ? `
        <div class="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5">
          <h3 class="text-white/50 text-xs uppercase tracking-widest mb-3">Riepilogo ordine</h3>
          <div class="space-y-0">${itemsHtml}</div>
          <div class="mt-3 pt-3 border-t border-white/10 space-y-1.5">
            ${shippingAmt > 0 ? `<div class="flex justify-between text-sm"><span class="text-white/50">${esc(shippingTitle)}</span><span class="text-white">&euro;${shippingAmt.toFixed(2).replace('.', ',')}</span></div>` : ''}
            <div class="flex justify-between text-sm font-bold pt-1"><span class="text-white">Totale pagato</span><span class="text-accent">&euro;${totalAmt.toFixed(2).replace('.', ',')}</span></div>
          </div>
        </div>` : '';

      const deliveryHtml = deliveryName ? `
        <div class="mt-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-left space-y-1">
          <p class="text-white/40 text-xs uppercase tracking-widest mb-2">Consegna</p>
          <p class="text-white">${deliveryName}</p>
          ${deliveryAddr ? `<p class="text-white/60">${deliveryAddr}</p>` : ''}
          ${deliveryCityLine ? `<p class="text-white/60">${deliveryCityLine}</p>` : ''}
          ${email ? `<p class="text-white/50 text-xs mt-1">${email}</p>` : ''}
        </div>` : '';

      // ── Sezione crea account ──
      const accountHtml = email ? `
        <div id="cc-account-wrap" class="mt-5 rounded-2xl border border-white/10 bg-white/5 px-5 py-5">
          <p class="text-white/50 text-xs uppercase tracking-widest mb-3">Accedi agli ordini in futuro</p>
          <p class="text-white/60 text-sm mb-4">Crea un account gratuito per tracciare questo e i prossimi ordini in qualsiasi momento.</p>
          <form id="cc-create-account-form" class="space-y-3" novalidate>
            <input type="email" value="${email}" disabled class="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white/50 text-sm cursor-not-allowed" />
            <input type="text" name="firstName" placeholder="Nome (opzionale)" value="${esc(sh.firstName || '')}" class="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-accent/50" autocomplete="given-name" />
            <input type="password" name="password" placeholder="Scegli una password (min. 10 caratteri)" class="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-accent/50" autocomplete="new-password" minlength="10" />
            <button type="submit" id="cc-register-btn" class="w-full py-3 rounded-xl bg-accent text-black font-semibold text-sm hover:bg-accent/90 transition-colors">Crea account</button>
            <p id="cc-register-msg" class="text-xs min-h-[1rem] text-center"></p>
          </form>
          <p class="text-white/30 text-xs text-center mt-3">Hai già un account? <a href="/pages/mio-account" class="underline hover:text-white/60">Accedi</a></p>
        </div>` : '';

      // ── Assembla HTML ──
      confirmedEl.className = 'text-center pt-4';
      confirmedEl.innerHTML = `
        <div class="w-16 h-16 mx-auto mb-5 rounded-full bg-green-500/10 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-9 h-9 text-green-400">
            <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd" />
          </svg>
        </div>
        <h2 class="text-xl font-bold text-white mb-1">Ordine Confermato!</h2>
        <p class="text-white/50 text-sm mb-1">Numero ordine: <span class="text-white font-semibold">${esc(orderName)}</span></p>
        <p class="text-white/40 text-xs mb-4">Riceverai una email di conferma all'indirizzo indicato.</p>
        <div class="text-left">${isPaymentHtml ? paymentMessage : `<p class="text-white/60 text-sm">${esc(paymentMessage)}</p>`}</div>
        ${summaryHtml}
        ${deliveryHtml}
        ${accountHtml}
        <a href="/collections" class="inline-block mt-6 px-6 py-3 rounded-xl bg-[#1A1A1A] border border-white/10 text-white font-medium hover:border-white/30 transition-colors text-sm">
          Torna al catalogo
        </a>
      `;

      // ── Listener form crea account ──
      const form = confirmedEl.querySelector('#cc-create-account-form');
      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const btn = form.querySelector('#cc-register-btn');
          const msgEl = form.querySelector('#cc-register-msg');
          const password = form.querySelector('[name="password"]')?.value || '';
          const firstName = form.querySelector('[name="firstName"]')?.value?.trim() || '';
          if (password.length < 10) { msgEl.className = 'text-xs min-h-[1rem] text-center text-red-400'; msgEl.textContent = 'La password deve avere almeno 10 caratteri'; return; }
          btn.disabled = true; btn.textContent = 'Creazione account…';
          msgEl.textContent = '';
          try {
            const res = await fetch(apiUrl + '/auth/register', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: orderData?.email, password, firstName, lastName: sh.lastName || '' }),
            });
            const data = await res.json();
            if (res.ok && data.accessToken) {
              localStorage.setItem('sc_access_token', data.accessToken);
              if (data.refreshToken) localStorage.setItem('sc_refresh_token', data.refreshToken);
              form.closest('#cc-account-wrap').innerHTML = '<p class="text-green-400 text-sm py-2">Account creato! <a href="/pages/mio-account" class="underline hover:text-green-300">Vai ai tuoi ordini &rarr;</a></p>';
            } else if (data.emailConfirmationRequired) {
              msgEl.className = 'text-xs min-h-[1rem] text-center text-green-400';
              msgEl.textContent = 'Controlla la tua email per confermare l\'account.';
              btn.disabled = false; btn.textContent = 'Crea account';
            } else if (data.errorCode === 'EMAIL_TAKEN') {
              msgEl.className = 'text-xs min-h-[1rem] text-center text-white/50';
              msgEl.innerHTML = 'Email già registrata. <a href="/pages/mio-account" class="underline hover:text-white">Accedi</a>';
              btn.disabled = false; btn.textContent = 'Crea account';
            } else {
              msgEl.className = 'text-xs min-h-[1rem] text-center text-red-400';
              msgEl.textContent = data.error || 'Errore durante la registrazione.';
              btn.disabled = false; btn.textContent = 'Crea account';
            }
          } catch {
            msgEl.className = 'text-xs min-h-[1rem] text-center text-red-400';
            msgEl.textContent = 'Errore di rete. Riprova.';
            btn.disabled = false; btn.textContent = 'Crea account';
          }
        });
      }

      // Scroll in cima
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // ── Analytics: evento Purchase ──
      try {
        const totalNum = parseFloat(orderData?.total || 0);
        const analyticsItems = (orderData?.items || []).map(i => ({
          item_id: String(i.variantId || ''),
          item_name: i.name || '',
          item_brand: i.brand || '',
          item_category: i.model || (i.isAccessory ? 'Accessorio' : ''),
          item_variant: i.variantTitle || '',
          price: parseFloat(i.price || 0),
          quantity: i.quantity || 1,
        }));
        const purchaseKey = `sc_purchase:${orderName}`;
        if (
          typeof gtag !== 'undefined'
          && sessionStorage.getItem(purchaseKey) !== '1'
        ) {
          gtag('event', 'purchase', {
            transaction_id: orderName,
            value: totalNum,
            currency: 'EUR',
            payment_type: method,
            shipping: parseFloat(orderData?.shippingLine?.price || 0),
            items: analyticsItems,
          });
          sessionStorage.setItem(purchaseKey, '1');
        }
        if (typeof fbq !== 'undefined') {
          fbq('track', 'Purchase', { value: totalNum, currency: 'EUR' });
        }
      } catch (_e) {}

      // Svuota carrello
      fetch('/cart/clear.js', { method: 'POST' }).catch(() => {});
      state.cartCount = 0;
      updateCartBadges();
    }

    function setLoading(btn, loading) {
      if (!btn) return;
      btn.disabled = loading;
      btn.classList.toggle('opacity-60', loading);
    }

    // Chiave di idempotenza: resta la stessa se il cliente riprova lo stesso ordine (ad esempio
    // dopo un errore di rete), cosi il Worker restituisce l'ordine gia creato invece di duplicarlo.
    function orderRequestKey(method, payload) {
      const storageKey = 'sc_order_key_' + method;
      const signature = JSON.stringify([
        payload.items.map((item) => [item.variantId, item.quantity]),
        payload.customerEmail,
        payload.shippingAddress.zip,
        payload.shippingAddress.address1,
      ]);
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (saved && saved.signature === signature) return saved.key;
        const key = window.crypto && crypto.randomUUID
          ? crypto.randomUUID()
          : (Date.now().toString(36) + Math.random().toString(36).slice(2)).padEnd(16, '0');
        sessionStorage.setItem(storageKey, JSON.stringify({ key, signature }));
        return key;
      } catch (e) {
        return null;
      }
    }

    function clearOrderRequestKey(method) {
      try { sessionStorage.removeItem('sc_order_key_' + method); } catch (e) {}
    }

    async function postOrder(method, payload) {
      const key = orderRequestKey(method, payload);
      const res = await fetch(apiUrl + '/order/' + method, {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, key ? { 'Idempotency-Key': key } : {}),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Errore creazione ordine');
      clearOrderRequestKey(method);
      return data;
    }

    // --- Bonifico ---
    const confirmBonifBtn = $('#confirm-bonifico-btn');
    if (confirmBonifBtn) {
      confirmBonifBtn.addEventListener('click', async () => {
        hideError();
        const shipping = getShippingData();
        if (!shipping) return;
        setLoading(confirmBonifBtn, true);

        try {
          const data = await postOrder('bonifico', { items: cartItems, shippingAddress: shipping, customerEmail: shipping.email, shippingLine });

          const bonCard = `<div class="mt-3 rounded-2xl border border-white/10 bg-white/5 p-5 text-left space-y-3">
            <p class="text-white/60 text-sm font-medium">Effettua un bonifico con i seguenti dati:</p>
            <div class="space-y-2">
              <div class="flex justify-between gap-2 text-sm">
                <span class="text-white/50 shrink-0">IBAN</span>
                <span class="text-white font-mono font-medium text-right">${data.bonifico?.iban}</span>
              </div>
              <div class="flex justify-between gap-2 text-sm">
                <span class="text-white/50 shrink-0">Intestatario</span>
                <span class="text-white font-medium text-right">${data.bonifico?.titolare}</span>
              </div>
              <div class="flex justify-between gap-2 text-sm">
                <span class="text-white/50 shrink-0">Banca</span>
                <span class="text-white font-medium text-right">${data.bonifico?.banca}</span>
              </div>
              <div class="flex justify-between gap-2 text-sm border-t border-white/10 pt-2">
                <span class="text-white/50 shrink-0">Importo</span>
                <span class="text-white font-bold">${formatEur(parseFloat(data.total))}</span>
              </div>
              <div class="flex justify-between gap-2 text-sm">
                <span class="text-white/50 shrink-0">Causale</span>
                <span class="text-green-400 font-mono font-semibold text-right">${data.bonifico?.causale}</span>
              </div>
            </div>
            <p class="text-white/40 text-xs">L'ordine verrà processato dopo la ricezione del pagamento.</p>
          </div>`;

          showConfirmed(data.orderName, bonCard, true, { items: cartItems, total: data.total, email: shipping.email, shippingLine, shipping });
        } catch (err) {
          showError(err.message + ' — riprova o contatta il supporto.');
          setLoading(confirmBonifBtn, false);
        }
      });
    }

    // --- Contrassegno ---
    const confirmCodBtn = $('#confirm-contrassegno-btn');
    if (confirmCodBtn) {
      confirmCodBtn.addEventListener('click', async () => {
        hideError();
        const shipping = getShippingData();
        if (!shipping) return;
        setLoading(confirmCodBtn, true);

        try {
          const data = await postOrder('contrassegno', { items: cartItems, shippingAddress: shipping, customerEmail: shipping.email, shippingLine });

          const codCard = `<div class="mt-3 rounded-2xl border border-white/10 bg-white/5 p-5 text-left space-y-3">
            <p class="text-white/70 text-sm">Il tuo ordine è confermato e sarà spedito entro le tempistiche di lavorazione previste.</p>
            <div class="flex justify-between gap-2 text-sm border-t border-white/10 pt-3">
              <span class="text-white/50 shrink-0">Totale da pagare al corriere</span>
              <span class="text-white font-bold">${formatEur(parseFloat(data.total))}</span>
            </div>
            <p class="text-white/50 text-xs">(di cui ${formatEur(parseFloat(data.fee))} spedizione con contrassegno)</p>
            <div class="rounded-xl bg-yellow-500/10 border border-yellow-500/20 p-4 mt-2">
              <p class="text-yellow-300/80 text-xs font-semibold mb-1">⚠ Importante — Contrassegno</p>
              <p class="text-yellow-200/60 text-xs leading-relaxed">L'ordine in Contrassegno è un completo ordine fiscale e richiede il ritiro obbligatorio, eccetto nel caso di annullamento dell'ordine prima della spedizione. Eventuali ordini non ritirati comporteranno l'emissione di una nota fiscale per la spedizione con contrassegno di €20.</p>
            </div>
          </div>`;

          showConfirmed(data.orderName, codCard, true, { items: cartItems, total: data.total, email: shipping.email, shippingLine, shipping });
        } catch (err) {
          showError(err.message + ' — riprova o contatta il supporto.');
          setLoading(confirmCodBtn, false);
        }
      });
    }

    // Init: carica il carrello e il riepilogo del metodo selezionato.
    loadCart()
      .catch(err => {
        showError('Errore nel caricamento del carrello: ' + err.message);
      });

    // Listener cambio metodo spedizione (solo standard attivo)
    $$('[name="shipping_method"]').forEach(input => {
      input.addEventListener('change', () => {
        shippingLine = { title: 'Express BRT', price: 0 };
        loadCart().catch(() => {});
      });
    });
  }

  /* ================================================
     SCELTA ALL'ACQUISTO (titolare 06/10)
     Card prodotto con più varianti (versione, porte, finitura grezza): la scelta imposta la variante del modulo,
     il prezzo mostrato e il nome nel riepilogo degli accessori. Senza scelta il pulsante resta spento.
     ================================================ */
  function initVariantPickers() {
    $$('[data-variant-picker]').forEach((picker) => {
      const card = picker.closest('article');
      const form = card && card.querySelector('.add-to-cart-form');
      if (!form) return;
      const input = form.querySelector('[name="id"]');
      const btn = form.querySelector('.add-to-cart-btn');
      const label = form.querySelector('[data-add-label]');
      const priceEl = card.querySelector('[data-card-price]');
      const pills = picker.querySelectorAll('.variant-pill');
      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          pills.forEach((p) => {
            const on = p === pill;
            p.classList.toggle('is-active', on);
            p.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          input.value = pill.dataset.variantId;
          form.dataset.productPrice = pill.dataset.price;
          form.dataset.productName = `${form.dataset.productBaseName} — ${pill.dataset.title}`;
          if (priceEl) priceEl.textContent = pill.dataset.priceText;
          if (label) label.textContent = `Aggiungi al Carrello — ${pill.dataset.priceText}`;
          if (btn) {
            btn.disabled = false;
            btn.classList.remove('opacity-50', 'cursor-not-allowed');
          }
          picker.querySelectorAll('[data-note-for]').forEach((note) => {
            note.classList.toggle('hidden', note.dataset.noteFor !== pill.dataset.variantId);
          });
        });
      });
    });
  }

  /* ================================================
     INIT
     ================================================ */
  onReady(() => {
    initMenu();
    initScroll();
    initBackButton();
    initFindKit();
    initCatalog();
    initGalleries();
    initLightbox();
    initVariantPickers();
    initCart();
    initAccessoryModal();
    initSchedaTecnica();
    initFloatingCartBar();
    initAnimations();
    initNotifyForms();
    initCheckoutPage();
  });

})();
